import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { formatDistanceToNow } from 'date-fns';
import { Phone, Mail, Eye, Download } from 'lucide-react';

interface Lead {
  id: string;
  email: string;
  phone?: string;
  onboarding_answers?: any;
  step_reached: number;
  converted_to_payment: boolean;
  payment_id?: string;
  created_at: string;
  updated_at: string;
}

interface LeadsListProps {
  leads: Lead[];
  loading: boolean;
}

export const LeadsList: React.FC<LeadsListProps> = ({ leads, loading }) => {
  const getStepName = (step: number) => {
    const stepNames = {
      8: 'Email/Phone Collection',
      9: 'Conversion Attempt',
      10: 'Payment Process',
      11: 'Completion'
    };
    return stepNames[step as keyof typeof stepNames] || `Step ${step}`;
  };

  const exportLeads = () => {
    const csvContent = [
      ['Email', 'Phone', 'Step Reached', 'Converted', 'Created Date'].join(','),
      ...leads.map(lead => [
        lead.email,
        lead.phone || '',
        getStepName(lead.step_reached),
        lead.converted_to_payment ? 'Yes' : 'No',
        new Date(lead.created_at).toLocaleDateString()
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5" />
          Leads ({leads.length})
        </CardTitle>
        <Button onClick={exportLeads} variant="outline" size="sm">
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </CardHeader>
      <CardContent>
        {leads.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No leads found
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Contact Info</TableHead>
                  <TableHead>Step Reached</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((lead) => (
                  <TableRow key={lead.id}>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{lead.email}</span>
                        </div>
                        {lead.phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">{lead.phone}</span>
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {getStepName(lead.step_reached)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant={lead.converted_to_payment ? "default" : "secondary"}
                      >
                        {lead.converted_to_payment ? "Converted" : "Lead"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {formatDistanceToNow(new Date(lead.created_at), { addSuffix: true })}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm">
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};